import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twMerge } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import { capitalizeFirstWord } from 'utilities/functions';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import CustomTooltip from '../../CustomTooltip';

export interface RadioOption {
  label: string;
  value: string | number;
  tooltip?: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  name: string;
  options: RadioOption[];
  selectedValue: string | null;
  onChange: (value: string | number) => void;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}

type ControlledRadioGroupProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  isRequired?: boolean;
  options: RadioOption[];
  disabled?: boolean;
};

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  options,
  selectedValue,
  onChange,
  error,
  disabled,
  className,
}) => {
  return (
    <div className={className} id={name}>
      {options.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center text-sm font-semibold mb-2.5 text-med-gray-3"
        >
          <input
            name={name}
            type="radio"
            disabled={disabled}
            value={option.value}
            checked={selectedValue === option.value}
            onChange={() => onChange(option.value)}
            className={twMerge(
              'appearance-none flex cursor-pointer shrink-0 h-7 w-7 rounded-full mr-2.5 border input-border__gray text-sm font-medium',
              'bg-white checked:border-clc-blue checked:border-[9px]',
              error && 'border-alert-negative bg-error-yellow',
            )}
          />
          {capitalizeFirstWord(option.label)}
          {option?.tooltip && (
            <InfoIcon
              className="w-5 h-5 min-w-[20px] fill-med-gray-3 ml-1.5"
              data-tooltip-id={option.value}
            />
          )}
          <CustomTooltip
            style={{ zIndex: 10 }}
            id={String(option.value)}
            place="right"
            className="max-w-[460px]"
            content={option?.tooltip}
          />
        </label>
      ))}
    </div>
  );
};

export const RadioBlockGroup: React.FC<RadioGroupProps> = ({
  name,
  options,
  selectedValue,
  onChange,
  error,
  disabled,
}) => {
  return (
    <div className="flex items-center gap-2.5 w-full">
      {options.map((option) => (
        <label
          key={option.value}
          className="cursor-pointer text-sm font-semibold mb-2.5 w-full text-med-gray-3"
        >
          <div className="flex items-center justify-items-start py-3 px-4 border border-med-gray-3 rounded-5 w-full">
            <input
              name={name}
              type="radio"
              disabled={disabled}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={() => onChange(option.value)}
              className={twMerge(
                'appearance-none flex cursor-pointer shrink-0 h-7 w-7 rounded-full mr-2.5 border input-border__gray text-sm font-medium',
                'bg-white checked:border-clc-blue checked:border-[9px]',
                error && 'border-alert-negative bg-error-yellow',
              )}
            />
            {capitalizeFirstWord(option.label)}
          </div>
        </label>
      ))}
    </div>
  );
};

const ControlledRadioGroup = <T extends FieldValues>({
  name,
  label,
  isRequired,
  options,
  disabled,
}: ControlledRadioGroupProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div className="flex w-full flex-col gap-0.5">
      <FieldLabel label={label} isRequired={isRequired} name={name} />
      <RadioGroup
        name={field.name}
        options={options}
        selectedValue={field.value}
        onChange={field.onChange}
        error={error?.message}
        disabled={disabled}
      />
      {error && <FieldError>{error.message}</FieldError>}
    </div>
  );
};

export const ControlledRadioBlockGroup = <T extends FieldValues>({
  name,
  label,
  isRequired,
  options,
  disabled,
}: ControlledRadioGroupProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div className="flex w-full flex-col gap-0.5">
      <FieldLabel label={label} isRequired={isRequired} name={name} />
      <RadioBlockGroup
        name={field.name}
        options={options}
        selectedValue={field.value}
        onChange={field.onChange}
        error={error?.message}
        disabled={disabled}
      />
      {error && <FieldError>{error.message}</FieldError>}
    </div>
  );
};

export default ControlledRadioGroup;
